"use client";
import React from "react";
import { Caveat } from "next/font/google";
import Image from "next/image";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export default function EyewearStorefrontSection({ section }) {
  const title = section?.title || "Your online storefront, 24/7";
  const leftFeature = section?.leftFeature || {
    bold: "Online prescription handling.",
    text: " Customers can buy glasses and contact lenses online, and add their prescription upon or after checkout."
  };
  const rightFeatures = section?.rightFeatures || [
    {
      bold: "Discounts and promotions.",
      text: " Run campaigns to reward your most loyal customers."
    },
    {
      bold: "Click and collect.",
      text: " Customers can order online and collect in-store."
    }
  ];
  const prescriptionImg = section?.prescriptionImg || "/odoo/industries/eyewear/prescription.png";
  const catalogImg = section?.catalogImg || "/odoo/industries/eyewear/catalog.png";

  return (
    <section className="py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className={`text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white inline-flex flex-wrap justify-center items-center gap-3 ${caveat.className}`}>
            <span>Your online storefront,</span>
            <span className="relative inline-block pb-2 text-white">
              24/7
              <svg
                className="absolute bottom-0 left-0 w-full h-3.5 text-amber-400 overflow-visible"
                viewBox="0 0 100 20"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M 5 14 Q 50 2, 95 12 T 190 12"
                  stroke="currentColor"
                  strokeWidth="6"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h2>
        </motion.div>

        {/* 2 Column Staggered Grid - Spanning equal container width */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* Left Column: Text Top + Expanded Left Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-start space-y-8 w-full"
          >
            {/* Top Text */}
            <div className="text-slate-300 text-lg md:text-xl leading-relaxed">
              <p>
                <strong className="text-white font-semibold">{leftFeature.bold}</strong>
                {leftFeature.text}
              </p>
            </div>

            {/* Expanded Left Image (Prescription) - Full Width with Preserved Height */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white transition-transform duration-300 hover:scale-[1.01]">
              <Image
                src={prescriptionImg}
                alt="Online prescription handling interface"
                width={800}
                height={450}
                className="w-full h-auto object-cover rounded-2xl"
                priority
              />
            </div>
          </motion.div>

          {/* Right Column: Expanded Right Image + Text Bottom */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-start space-y-8 w-full"
          >
            {/* Expanded Right Image (Catalog) - Full Width with Preserved Height */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white transition-transform duration-300 hover:scale-[1.01]">
              <Image
                src={catalogImg}
                alt="Online storefront catalog interface"
                width={800}
                height={450}
                className="w-full h-auto object-cover rounded-2xl"
                priority
              />
            </div>

            {/* Bottom Features Text */}
            <div className="space-y-6 text-slate-300 text-lg md:text-xl leading-relaxed pt-2">
              {rightFeatures.map((item, idx) => (
                <p key={idx}>
                  <strong className="text-white font-semibold">{item.bold}</strong>
                  {item.text}
                </p>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}